/**
 * Quotation Builder Page
 *
 * Create and edit quotations with dynamic item management
 * and real-time Wave Calculator integration
 *
 * Supports "create_project" mode where quotation creates its own project.
 *
 * @module pages/quotations/QuotationBuilder
 */

import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Table,
  Alert,
  Spinner,
  Modal,
  Dropdown,
} from 'react-bootstrap';
import { toast } from '../../utils/toast';
import {
  createQuotation,
  getQuotation,
  updateQuotation,
  deleteQuotation,
  submitQuotationForReview,
  markQuotationSent,
  approveQuotation,
  rejectQuotation,
  assignLiderToQuotation,
} from '../../services/quotationService';
import { listProjects } from '../../services/projectService';
import { listClients } from '../../services/clientService';
import { listAssignableUsers } from '../../services/userService';
import {
  createProjectWithQuotation,
  updateProjectWithQuotation,
} from '../../services/projectQuotationService';
import { getProjectTypes } from '../../services/projectTypeService';
import { calculateQuotationTotals, validateQuotationData } from '../../utils/calculations';
import { formatCurrency } from '../../utils/formatters';
import { DEFAULT_TAX_RATE, ITEM_TYPE, QUOTATION_STATUS } from '../../utils/constants';
import { useAuth } from '../../contexts/AuthContext';
import QuotationItemRow from './QuotationItemRow';
import QuotationItemCard from './QuotationItemCard';
import {
  isPriceUnconfirmed,
  unconfirmedPriceItems,
  confirmUnconfirmedPrices,
} from './priceSourceDisplay';
import { PRICE_ROW_SOURCE } from '../../utils/priceProvenance';
import DashboardLayout from '../../components/layout/DashboardLayout';
import KebabMenu from '../../components/common/KebabMenu';
import './QuotationBuilder.css';

/**
 * #618 — "alguien eligió 0" vs "nadie lo definió", sin coerciones.
 *
 * `Number(null)` es `0` y `null == 0` es `false` pero `null >= 0` es `true`:
 * ninguna comparación numérica sirve acá, porque todas contestan sobre un valor
 * que primero hay que saber si EXISTE. Esto pregunta eso primero. El `!= null`
 * suelto es deliberado: cubre `null` y `undefined`, y nada más.
 *
 * @param {*} valor - porcentaje leído de la base (numeric nullable) o del form
 * @returns {boolean} true sólo si hay un valor y ese valor es 0
 */
const esCeroDecidido = (valor) => valor != null && valor !== '' && Number(valor) === 0;

/**
 * QuotationBuilder component
 *
 * @param {Object} props - Component props
 * @param {boolean} props.embedded - If true, runs in embedded mode (no breadcrumbs, custom callbacks)
 * @param {string} props.mode - Mode: 'create_project' | 'edit' | 'standard' (default)
 * @param {Function} props.onSuccess - Callback when quotation is saved successfully
 */
const QuotationBuilder = ({ embedded = false, mode = 'standard', onSuccess = null }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id: quotationId } = useParams();
  const [searchParams] = useSearchParams();
  const projectIdParam = searchParams.get('projectId');
  const { user, isAdmin, isSupervisor, isTechnician, loading: authLoading } = useAuth();
  const isEditMode = !!quotationId;
  const isCreateProjectMode = mode === 'create_project';

  const { data: projectTypes = [] } = useQuery({
    queryKey: ['project_types'],
    queryFn: () => getProjectTypes(),
    staleTime: 1000 * 60 * 10,
  });

  // Form state - Enhanced for project creation mode
  const [projectTitle, setProjectTitle] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [clientId, setClientId] = useState('');
  // #462: holds a project_types.id (uuid), not a code string — passed
  // straight through as project_type_id so the DB's set_project_number
  // trigger can build the "tipo" segment of project_number. Empty until
  // the catalog loads (see the auto-select effect below).
  const [projectType, setProjectType] = useState('');
  const [projectId, setProjectId] = useState(projectIdParam || '');
  const [items, setItems] = useState([
    {
      item_type: ITEM_TYPE.MATERIAL,
      description: '',
      quantity: 1,
      unit: 'unidad',
      unit_price: 0,
    },
  ]);
  // M1: '' means "nobody has set a margin yet" — NOT zero. These used to start
  // at 0, which is why 24 of the 35 quotations in prod carry risk=0/profit=0
  // (the two most recent ones, 2026-07-27, among them) with nothing anywhere
  // recording that it was a choice. See `validateQuotationData`.
  const [riskPercentage, setRiskPercentage] = useState('');
  const [profitPercentage, setProfitPercentage] = useState('');
  // Explicit acknowledgement that this quotation goes out at cost. It is the
  // single mechanism that turns an omission into a decision — and the only
  // thing a technician can do here, since fixing the numbers stays admin-only.
  const [zeroMarginAck, setZeroMarginAck] = useState(false);
  // #609 — el gemelo del ack de margen, para el otro campo que se puede
  // inventar. Marcarlo es lo que convierte "el asistente puso este número" en
  // "una persona lo miró y lo firma"; sin marcarlo, la línea sigue trabada y la
  // cotización no se puede aprobar ni enviar.
  const [unconfirmedPricesAck, setUnconfirmedPricesAck] = useState(false);
  const [taxRate, setTaxRate] = useState(DEFAULT_TAX_RATE);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetchingQuotation, setFetchingQuotation] = useState(isEditMode);

  // RACI selectors (#129): supervisor + lider for the quotation
  const [supervisorId, setSupervisorId] = useState('');
  const [liderId, setLiderId] = useState('');
  const [supervisorName, setSupervisorName] = useState('');
  const [liderName, setLiderName] = useState('');

  // Reassign Lider modal state
  const [showReassignLiderModal, setShowReassignLiderModal] = useState(false);
  const [reassignLiderId, setReassignLiderId] = useState('');
  const [reassigning, setReassigning] = useState(false);

  // Send to Client modal state
  const [showSendModal, setShowSendModal] = useState(false);
  const [quotationStatus, setQuotationStatus] = useState(null);
  const [clientEmail, setClientEmail] = useState('');

  // Delete modal state (double confirmation)
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Approval/Rejection state
  const [approving, setApproving] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejecting, setRejecting] = useState(false);

  // Fetch projects for dropdown (standard mode or edit mode)
  const { data: projectsData, isLoading: loadingProjects } = useQuery({
    queryKey: ['projects', { pageSize: 100 }],
    queryFn: () => listProjects({ pageSize: 100 }),
    enabled: !isCreateProjectMode,
  });

  const projects = projectsData?.data || [];

  // Fetch clients for dropdown (only in create project mode)
  const { data: clientsData, isLoading: loadingClients } = useQuery({
    queryKey: ['clients'],
    queryFn: () => listClients(),
    enabled: isCreateProjectMode,
  });

  const clients = useMemo(() => clientsData?.data || [], [clientsData]);

  // #462: default the type select to the first available catalog entry
  // once it loads, mirroring the old hardcoded 'SL' default — but now
  // storing the real project_types.id instead of a code string. Only
  // fires for a brand-new project (not when editing/hydrating from an
  // existing quotation).
  useEffect(() => {
    if (!isEditMode && !projectType && projectTypes.length > 0) {
      setProjectType(projectTypes[0].id);
    }
  }, [isEditMode, projectType, projectTypes]);

  // #462: cosmetic-only preview of the segments the DB will compose into
  // project_number on save. The consecutivo itself is NOT previewable —
  // project_sequences has zero RLS policies for authenticated/anon (#466),
  // by design, so only the SECURITY DEFINER DB function can read/advance
  // it. "###" stands in for that atomic part; the final number is
  // whatever generate_project_number() actually returns on insert.
  const projectNumberPreview = useMemo(() => {
    if (!isCreateProjectMode || isEditMode) return null;
    const selectedClient = clients.find((c) => c.id === clientId);
    const selectedType = projectTypes.find((t) => t.id === projectType);
    const clientSeg = selectedClient?.client_code || 'XX';
    const typeSeg = selectedType?.code || 'XX';
    const now = new Date();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const yy = String(now.getFullYear()).slice(-2);
    return `###-${clientSeg}-${typeSeg}-${mm}${yy}`;
  }, [isCreateProjectMode, isEditMode, clients, clientId, projectTypes, projectType]);

  // Fetch users for RACI selectors (#129)
  // Supervisors: admin sees admin+supervisor, supervisor sees self-only (no need to query)
  const { data: supervisorsData = { data: [] } } = useQuery({
    queryKey: ['assignable_users', 'supervisor'],
    queryFn: () => listAssignableUsers(['supervisor', 'admin']),
    enabled: !isTechnician, // technicians can't create quotations
    staleTime: 1000 * 60 * 5,
  });
  const supervisors = supervisorsData?.data || [];

  const { data: lideresData = { data: [] } } = useQuery({
    queryKey: ['assignable_users', 'lider'],
    queryFn: () => listAssignableUsers(['technician']),
    enabled: !isTechnician,
    staleTime: 1000 * 60 * 5,
  });
  const lideres = lideresData?.data || [];

  // Auto-fill supervisor if caller is supervisor (and not editing)
  useEffect(() => {
    if (!isEditMode && isSupervisor && user?.id && !supervisorId) {
      setSupervisorId(user.id);
    }
  }, [isEditMode, isSupervisor, user, supervisorId]);

  // Fetch quotation if editing
  const { data: quotationData, isLoading: loadingQuotation } = useQuery({
    queryKey: ['quotation', quotationId],
    queryFn: () => getQuotation(quotationId),
    enabled: isEditMode && !!quotationId,
  });

  // Handle quotation data when loaded
  useEffect(() => {
    if (quotationData?.data) {
      const q = quotationData.data;
      setProjectId(q.project_id);
      setItems(q.quotation_items || []);
      // M1: `??`, not `||` — same reasoning as the tax rate below. A stored 0
      // is a margin somebody set; only a NULL is "never set". (In practice the
      // column is `DEFAULT 0`, so NULL only shows up on legacy rows.)
      setRiskPercentage(q.risk_percentage ?? '');
      setProfitPercentage(q.profit_percentage ?? '');
      // Re-opening a saved 0/0 quotation counts as already acknowledged: the
      // decision was taken when it was saved, and re-asking would be noise.
      //
      // #618: esto era `Number(q.risk_percentage) === 0 && ...`, y `Number(null)`
      // es `0`. O sea que una cotización cuyo margen NADIE definió se abría con
      // el visto de "se emite a costo" YA MARCADO — la distinción de #593
      // borrada por una coerción, justo en el único lugar donde un humano
      // confirma la decisión. Y no quedaba en la pantalla: con el ack en true,
      // `resolvedRisk/ProfitPercentage` (abajo) dejan de ser `undefined` y pasan
      // a 0, así que abrir el borrador y apretar "guardar" escribía 0/0 encima
      // del NULL y destrababa la compuerta de envío sin que nadie decidiera nada.
      // Medido contra la base local antes del arreglo: NULL -> 0/0.
      //
      // La comparación tiene que preguntar PRIMERO si el valor existe. `!= null`
      // (suelto, a propósito) cubre null y undefined y nada más.
      setZeroMarginAck(esCeroDecidido(q.risk_percentage) && esCeroDecidido(q.profit_percentage));
      // #457: `??`, not `||`. 0% IVA is a legitimate saved value (exempt
      // work), and `0 || DEFAULT_TAX_RATE` silently reopened the quotation at
      // 13%. Only a NULL tax_rate ("never set") falls back to the default.
      setTaxRate(q.tax_rate ?? DEFAULT_TAX_RATE);
      setNotes(q.notes || '');
      setQuotationStatus(q.status);

      // RACI: hydrate supervisor/lider from quotation (#129)
      setSupervisorId(q.supervisor_id || '');
      setLiderId(q.lider_id || '');
      setSupervisorName(q.supervisor?.full_name || '');
      setLiderName(q.lider?.full_name || '');

      // Extract project data if in edit mode
      if (q.project) {
        setProjectTitle(q.project.title);
        setProjectDescription(q.project.description || '');
        setClientId(q.project.client_id);

        // #A4: leía `client.email`, que no existe por partida doble: el
        // `.select()` de getQuotation no lo pedía Y `clients` no tiene esa
        // columna (se llama `primary_contact_email`). Resultado: "No email
        // disponible" siempre, para todas las cotizaciones — un campo que
        // nunca podía tener un valor.
        if (q.project.client) {
          setClientEmail(q.project.client.primary_contact_email || 'Sin correo registrado para este cliente');
        }
      }
      setFetchingQuotation(false);
    }
  }, [quotationData]);

  // Update fetching state based on loading
  useEffect(() => {
    if (isEditMode && !loadingQuotation && !quotationData) {
      setFetchingQuotation(false);
    }
  }, [isEditMode, loadingQuotation, quotationData]);

  /**
   * Check permissions
   */
  useEffect(() => {
    if (authLoading) return; // wait for profile to finish loading
    if (!isAdmin && !isTechnician) {
      toast.error('No tienes permisos para crear o editar cotizaciones');
      navigate('/');
    }
  }, [authLoading, isAdmin, isTechnician, navigate]);

  /**
   * M1 — margin resolution.
   *
   * `riskPercentage`/`profitPercentage` hold `''` while nobody has set them.
   * `undefined` here means "still unset" and is what gets handed to the
   * services, which write it as an explicit NULL (#593). Ticking the
   * zero-margin acknowledgement is what converts an unset field into a
   * DELIBERATE 0 — the distinction the whole lote is about.
   *
   * #593: guardar un borrador SIN margen ya no es un error. El RACI pone el
   * margen en el paso 3 (Administración), después de que el Líder cargó los
   * ítems; el borrador sin definir es el estado normal de los pasos 1 y 2. Lo
   * que sigue exigiendo confirmación es el 0 explícito — emitir a costo — y lo
   * que sigue bloqueado, más adelante, es ENVIAR sin definirlo.
   *
   * This also decouples the two permissions that were conflated: the inputs
   * stay admin-only (setting the margin is a commercial decision), but every
   * role that can reach this screen — the access gate below admits technicians
   * too — can now SEE that the quotation is heading out at cost, and has to
   * say so on the record before it ships.
   */
  const resolvedRiskPercentage = useMemo(
    () => (riskPercentage === '' ? (zeroMarginAck ? 0 : undefined) : Number(riskPercentage)),
    [riskPercentage, zeroMarginAck]
  );
  const resolvedProfitPercentage = useMemo(
    () => (profitPercentage === '' ? (zeroMarginAck ? 0 : undefined) : Number(profitPercentage)),
    [profitPercentage, zeroMarginAck]
  );

  /**
   * #593 — "sin definir" y "a costo" dejan de ser lo mismo en pantalla.
   *
   * Esto era `(resolvedRiskPercentage ?? 0) === 0 && ...`, o sea que un campo
   * vacío se leía como 0 y disparaba la advertencia de "se cobra el costo"
   * sobre una cotización que nadie había fijado todavía — exactamente la
   * confusión que este cambio existe para deshacer.
   */
  const isMarginUndefined =
    resolvedRiskPercentage === undefined || resolvedProfitPercentage === undefined;

  /** True when this quotation would be issued at cost (a DELIBERATE 0% risk AND 0% profit). */
  const isZeroMargin =
    !isMarginUndefined &&
    resolvedRiskPercentage === 0 && resolvedProfitPercentage === 0;

  /**
   * #609 — las líneas que traban la cotización, ANTES de intentar enviarla.
   *
   * Hasta acá la compuerta existía sólo del lado del servidor: el admin
   * apretaba "enviar", se comía un rechazo y recién ahí se enteraba de que
   * había un problema — sin ver en cuál de las diez líneas. Esto es el cartel
   * que le faltaba a esa pared.
   *
   * `unconfirmedPricesAck` no cambia la lista (esa es la verdad de la base);
   * cambia lo que se ESCRIBE al guardar, más abajo.
   */
  const unconfirmedItems = useMemo(() => unconfirmedPriceItems(items), [items]);
  const hasUnconfirmedPrices = unconfirmedItems.length > 0;

  /**
   * #609 — lo que se MANDA a guardar. Idéntico a `items`, salvo que el ack esté
   * marcado: ahí las líneas trabadas salen como `stated`, porque un humano
   * acaba de firmar esos montos en pantalla.
   *
   * Sin el ack se manda `price_source` tal cual vino, y eso importa: los
   * servicios del carril web BORRAN y REINSERTAN todas las líneas al guardar,
   * así que si no se reenviara la procedencia, apretar "guardar borrador"
   * dejaría en NULL una línea que estaba `unconfirmed` — o sea, apagaría la
   * compuerta sin que nadie decidiera nada.
   */
  const itemsToPersist = useMemo(
    () => (unconfirmedPricesAck ? confirmUnconfirmedPrices(items) : items),
    [items, unconfirmedPricesAck]
  );

  /**
   * Calculate totals in real-time
   */
  const totals = useMemo(() => {
    return calculateQuotationTotals(
      items,
      resolvedRiskPercentage ?? 0,
      resolvedProfitPercentage ?? 0,
      taxRate
    );
  }, [items, resolvedRiskPercentage, resolvedProfitPercentage, taxRate]);

  /**
   * Handle item update
   *
   * #609 — si lo que cambió es el PRECIO, la línea deja de estar sin
   * confirmar: acabás de tipear el número vos. Es la misma regla que
   * `updateQuotationItem` ya aplica del lado del servicio, acá arriba para que
   * el badge se apague en el momento y no al recargar. Lo demás (descripción,
   * cantidad, unidad) NO confirma nada: corregirle la redacción a una línea no
   * dice nada sobre su monto.
   */
  const handleItemUpdate = (index, updatedItem) => {
    const previous = items[index];
    const priceChanged =
      previous && Number(previous.unit_price) !== Number(updatedItem.unit_price);
    const next =
      priceChanged && isPriceUnconfirmed(previous)
        ? { ...updatedItem, price_source: PRICE_ROW_SOURCE.STATED }
        : updatedItem;

    const newItems = [...items];
    newItems[index] = next;
    setItems(newItems);
  };

  /**
   * Handle add item
   */
  const handleAddItem = () => {
    setItems([
      ...items,
      {
        item_type: ITEM_TYPE.MATERIAL,
        description: '',
        quantity: 1,
        unit: 'unidad',
        unit_price: 0,
      },
    ]);
  };

  /**
   * Handle remove item
   */
  const handleRemoveItem = (index) => {
    if (items.length === 1) {
      toast.warning('Debe haber al menos un artículo en la cotización');
      return;
    }
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
  };

  /**
   * Handle duplicate item
   */
  const handleDuplicateItem = (index) => {
    const itemToDuplicate = { ...items[index] };
    const newItems = [...items];
    newItems.splice(index + 1, 0, itemToDuplicate);
    setItems(newItems);
  };

  /**
   * Validate form based on mode
   */
  const validateForm = () => {
    if (isCreateProjectMode) {
      // Validate project creation fields
      if (!projectTitle) {
        toast.error('El título del proyecto es requerido');
        return false;
      }
      if (!clientId) {
        toast.error('Debes seleccionar un cliente');
        return false;
      }
    } else {
      // Standard mode: validate project selection
      if (!projectId) {
        toast.error('Debes seleccionar un proyecto');
        return false;
      }
    }

    // M1: a quotation at cost must be acknowledged, never assumed. This is the
    // gate that stops the silent zero from shipping — and it is the one that
    // matters for a technician, who cannot edit the numbers at all and used to
    // send 0/0 without the fields ever appearing on screen.
    if (isZeroMargin && !zeroMarginAck) {
      toast.error(
        'Esta cotización va sin margen (0% riesgo, 0% ganancia). Fijá el margen o confirmá que se emite a costo.'
      );
      return false;
    }

    const validation = validateQuotationData({
      items,
      riskPercentage: resolvedRiskPercentage,
      profitPercentage: resolvedProfitPercentage,
      taxRate,
    });

    if (!validation.isValid) {
      toast.error(validation.errors[0]);
      return false;
    }

    return true;
  };

  /**
   * Invalidate all caches affected by a quotation change so detail/list
   * pages refetch fresh data instead of serving stale cached values.
   */
  const invalidateQuotationCaches = (targetProjectId) => {
    queryClient.invalidateQueries(['quotations']);
    queryClient.invalidateQueries(['projects']);
    if (quotationId) {
      queryClient.invalidateQueries(['quotation', quotationId]);
    }
    if (targetProjectId) {
      queryClient.invalidateQueries(['project', targetProjectId]);
    }
  };

  /**
   * Handle save as draft
   */
  const handleSaveDraft = async () => {
    // Guard: project must be selected (standard mode)
    if (!isCreateProjectMode && !projectId) {
      toast.error('Selecciona un proyecto primero');
      return;
    }

    // Guard: at least one item with a description must exist
    const hasRealItems = items.some((item) => item.description && item.description.trim() !== '');
    if (!hasRealItems) {
      toast.warning('Agrega al menos un artículo antes de guardar');
      return;
    }

    if (!validateForm()) return;

    try {
      setLoading(true);

      let result;

      if (isCreateProjectMode && !isEditMode) {
        // Create project with quotation
        const projectData = {
          title: projectTitle,
          description: projectDescription,
          client_id: clientId,
          project_type_id: projectType || null,
          quotation_items: itemsToPersist,
          risk_percentage: resolvedRiskPercentage,
          profit_percentage: resolvedProfitPercentage,
          tax_rate: taxRate,
          notes,
          initial_status: 'draft',
          // RACI handoff (#129)
          supervisor_id: supervisorId || undefined,
          lider_id: liderId || undefined,
        };

        result = await createProjectWithQuotation(projectData);

        if (result.error) throw result.error;

        toast.success(
          `Proyecto ${result.data.projectNumber} creado con cotización asociada`
        );

        invalidateQuotationCaches(result.data.project.id);

        // If embedded mode and onSuccess callback provided, call it
        if (embedded && onSuccess) {
          onSuccess(result.data);
        } else {
          navigate(`/proyectos/${result.data.project.id}`);
        }

      } else if (isEditMode) {
        // Update existing quotation
        const quotationData = {
          project_id: projectId,
          items: itemsToPersist,
          risk_percentage: resolvedRiskPercentage,
          profit_percentage: resolvedProfitPercentage,
          tax_rate: taxRate,
          notes,
          // RACI handoff (#129) — admin only can reassign supervisor;
          // supervisor + admin can reassign lider via separate modal
          ...(isAdmin && supervisorId ? { supervisor_id: supervisorId } : {}),
          ...(liderId ? { lider_id: liderId } : {}),
        };

        // If we have project fields, update them too.
        // title/description/client_id are dispatched to the projects UPDATE
        // inside projectQuotationService.updateProjectWithQuotation — they
        // do NOT live on the quotations row. See wiki decision-quotation-
        // client-transit.md for the data-model rationale.
        if (isCreateProjectMode) {
          quotationData.title = projectTitle;
          quotationData.description = projectDescription;
          quotationData.client_id = clientId;
          quotationData.quotation_items = items;

          result = await updateProjectWithQuotation(
            projectId,
            quotationId,
            quotationData
          );
        } else {
          result = await updateQuotation(quotationId, quotationData);
        }

        if (result.error) throw result.error;

        toast.success('Cotización actualizada exitosamente');

        invalidateQuotationCaches(projectId);

        // If embedded mode and onSuccess callback provided, call it
        if (embedded && onSuccess) {
          onSuccess(result.data);
        } else {
          navigate(`/proyectos/${projectId}`);
        }

      } else {
        // Standard mode: create quotation for existing project
        const quotationData = {
          project_id: projectId,
          items: itemsToPersist,
          risk_percentage: resolvedRiskPercentage,
          profit_percentage: resolvedProfitPercentage,
          tax_rate: taxRate,
          notes,
          // RACI handoff (#129)
          supervisor_id: supervisorId || undefined,
          lider_id: liderId || undefined,
        };

        result = await createQuotation(quotationData);

        if (result.error) throw result.error;

        toast.success('Cotización guardada como borrador');

        invalidateQuotationCaches(projectId);

        // If embedded mode and onSuccess callback provided, call it
        if (embedded && onSuccess) {
          onSuccess(result.data);
        } else {
          navigate(`/proyectos/${projectId}`);
        }
      }

    } catch (error) {
      console.error('Save draft error:', error);
      toast.error(error.message || 'Error al guardar la cotización');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle submit for review
   */
  const handleSubmitForReview = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);

      let result;

      if (isCreateProjectMode && !isEditMode) {
        // Create project with quotation in review status
        const projectData = {
          title: projectTitle,
          description: projectDescription,
          client_id: clientId,
          project_type_id: projectType || null,
          quotation_items: itemsToPersist,
          risk_percentage: resolvedRiskPercentage,
          profit_percentage: resolvedProfitPercentage,
          tax_rate: taxRate,
          notes,
          initial_status: 'under_review',
          // RACI handoff (#129)
          supervisor_id: supervisorId || undefined,
          lider_id: liderId || undefined,
        };

        result = await createProjectWithQuotation(projectData);

        if (result.error) throw result.error;

        // Create review task for admins
        const reviewResult = await submitQuotationForReview(result.data.quotation.id);
        if (reviewResult.error) {
          console.warn('Review task creation failed:', reviewResult.error);
        }

        // #555 — el `warning` se RENDERIZA. `submitQuotationForReview` avisa
        // cuando la tarea de revisión no se pudo asignar o no se pudo
        // notificar; sin esto la cotización quedaba en `under_review`, el
        // toast decía "enviada para revisión" y ningún admin se enteraba —
        // exactamente el éxito mudo que #555 midió (0 notificaciones de tarea
        // sobre 21 asignaciones en prod). Mismo contrato que ya usan TaskForm
        // y TasksPage con el warning de `createTask`.
        if (reviewResult.warning) {
          toast.warning(reviewResult.warning);
        }

        toast.success(
          `Proyecto ${result.data.projectNumber} creado y enviado para revisión`
        );

        invalidateQuotationCaches(result.data.project.id);

        // If embedded mode and onSuccess callback provided, call it
        if (embedded && onSuccess) {
          onSuccess(result.data);
        } else {
          navigate(`/proyectos/${result.data.project.id}`);
        }

      } else {
        // Standard flow for existing projects
        const quotationData = {
          project_id: projectId,
          items: itemsToPersist,
          risk_percentage: resolvedRiskPercentage,
          profit_percentage: resolvedProfitPercentage,
          tax_rate: taxRate,
          notes,
          // RACI handoff (#129)
          ...(isAdmin && supervisorId ? { supervisor_id: supervisorId } : {}),
          ...(liderId ? { lider_id: liderId } : {}),
          ...(!isEditMode && supervisorId ? { supervisor_id: supervisorId } : {}),
        };

        if (isEditMode) {
          result = await updateQuotation(quotationId, quotationData);
          if (result.error) throw result.error;
          result = await submitQuotationForReview(quotationId);
        } else {
          result = await createQuotation(quotationData);
          if (result.error) throw result.error;
          result = await submitQuotationForReview(result.data.id);
        }

        if (result.error) throw result.error;

        // #555 — ver el comentario del otro camino: el warning se renderiza,
        // no se descarta.
        if (result.warning) {
          toast.warning(result.warning);
        }

        toast.success('Cotización enviada para revisión');

        invalidateQuotationCaches(projectId);

        // If embedded mode and onSuccess callback provided, call it
        if (embedded && onSuccess) {
          onSuccess(result.data);
        } else {
          navigate(`/proyectos/${projectId}`);
        }
      }

    } catch (error) {
      console.error('Submit for review error:', error);
      toast.error(error.message || 'Error al enviar la cotización');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle cancel / clear form
   */
  const handleCancel = () => {
    if (embedded) {
      // In embedded mode, reset the form instead of navigating
      setProjectTitle('');
      setProjectDescription('');
      setClientId('');
      setProjectType('');
      setProjectId('');
      setItems([
        {
          item_type: ITEM_TYPE.MATERIAL,
          description: '',
          quantity: 1,
          unit: 'unidad',
          unit_price: 0,
        },
      ]);
      setRiskPercentage(0);
      setProfitPercentage(0);
      setTaxRate(DEFAULT_TAX_RATE);
      setNotes('');
    } else {
      // In standalone mode, navigate back
      if (projectId) {
        navigate(`/proyectos/${projectId}`);
      } else {
        navigate('/proyectos');
      }
    }
  };

  /**
   * Handle send to client
   */
  const handleSendToClient = () => {
    setShowSendModal(true);
  };

  /**
   * Handle confirm send to client
   */
  const handleConfirmSend = async () => {
    try {
      setLoading(true);

      const result = await markQuotationSent(quotationId);

      if (result.error) throw result.error;

      toast.success('Cotización marcada como enviada');
      setShowSendModal(false);
      setQuotationStatus(QUOTATION_STATUS.SENT);

      invalidateQuotationCaches(projectId);

      // Navigate back to project
      navigate(`/proyectos/${projectId}`);
    } catch (error) {
      console.error('Send to client error:', error);
      toast.error('Error al enviar la cotización');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle close send modal
   */
  const handleCloseSendModal = () => {
    setShowSendModal(false);
  };

  /**
   * Handle approve quotation (admin only)
   */
  const handleApproveQuotation = async () => {
    try {
      setApproving(true);
      const result = await approveQuotation(quotationId);

      if (result.error) throw result.error;

      toast.success('Cotización aprobada exitosamente');
      setQuotationStatus(QUOTATION_STATUS.APPROVED);

      invalidateQuotationCaches(projectId);

      if (projectId) {
        navigate(`/proyectos/${projectId}`);
      }
    } catch (error) {
      console.error('Approve quotation error:', error);
      toast.error(error.message || 'Error al aprobar la cotización');
    } finally {
      setApproving(false);
    }
  };

  /**
   * Handle reject quotation (admin only)
   */
  const handleRejectQuotation = async () => {
    if (!rejectReason.trim()) {
      toast.error('Debes proporcionar una razón para el rechazo');
      return;
    }

    try {
      setRejecting(true);
      const result = await rejectQuotation(quotationId, rejectReason);

      if (result.error) throw result.error;

      toast.success('Cotización rechazada');
      setShowRejectModal(false);
      setRejectReason('');
      setQuotationStatus(QUOTATION_STATUS.REJECTED);

      invalidateQuotationCaches(projectId);

      if (projectId) {
        navigate(`/proyectos/${projectId}`);
      }
    } catch (error) {
      console.error('Reject quotation error:', error);
      toast.error(error.message || 'Error al rechazar la cotización');
    } finally {
      setRejecting(false);
    }
  };

  /**
   * Handle reassign lider (#129)
   * Available to supervisor (of this quotation) or admin.
   */
  const handleReassignLider = async () => {
    if (!reassignLiderId) {
      toast.error('Selecciona un líder técnico');
      return;
    }
    try {
      setReassigning(true);
      const result = await assignLiderToQuotation(quotationId, reassignLiderId);
      if (result.error) throw result.error;

      const picked = lideres.find((l) => l.id === reassignLiderId);
      setLiderId(reassignLiderId);
      setLiderName(picked?.full_name || '');
      toast.success('Líder técnico reasignado');
      setShowReassignLiderModal(false);
      setReassignLiderId('');
      invalidateQuotationCaches(projectId);
    } catch (error) {
      console.error('Reassign lider error:', error);
      toast.error(error.message || 'No se pudo reasignar al líder');
    } finally {
      setReassigning(false);
    }
  };

  /**
   * Handle delete quotation (double confirmation)
   */
  const handleDeleteQuotation = async () => {
    try {
      setDeleting(true);
      const result = await deleteQuotation(quotationId);

      if (!result.success) throw result.error;

      toast.success('Cotización eliminada exitosamente');
      setShowDeleteModal(false);

      invalidateQuotationCaches(projectId);
      if (quotationId) {
        queryClient.removeQueries(['quotation', quotationId]);
      }

      if (projectId) {
        navigate(`/proyectos/${projectId}`);
      } else {
        navigate('/cotizaciones');
      }
    } catch (error) {
      console.error('Delete quotation error:', error);
      toast.error(error?.message || 'Error al eliminar la cotización');
    } finally {
      setDeleting(false);
    }
  };

  // Loading state
  if (fetchingQuotation) {
    return (
      <DashboardLayout>
        <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Cargando cotización...</span>
          </Spinner>
        </Container>
      </DashboardLayout>
    );
  }

  // Human-readable name of the project/quotation this builder is editing,
  // for the kebab menu's accessible name (#477) -- create-project mode has
  // no project record yet, so fall back to the in-progress title typed by
  // the user, then to the selected existing project's title.
  const kebabProjectLabel =
    (isCreateProjectMode ? projectTitle : projects.find((p) => p.id === projectId)?.title) ||
    (isEditMode ? 'la cotización' : 'la nueva cotización');

  return (
    <DashboardLayout>
      <Container fluid className="py-4">
      <Row>
        <Col>
          <Card>
            <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
              <h4 className="mb-0">
                <i className={`bi ${isEditMode ? 'bi-pencil-square' : 'bi-plus-circle'} me-2`}></i>
                {isEditMode
                  ? 'Editar Cotización'
                  : isCreateProjectMode
                    ? 'Nuevo Proyecto con Cotización'
                    : 'Nueva Cotización'}
              </h4>
              <KebabMenu
                label={`Acciones de ${kebabProjectLabel}`}
                toggleClassName="text-white"
                iconClassName="fs-5"
              >
                <Dropdown.Item
                  onClick={handleSaveDraft}
                  disabled={loading}
                  data-testid="quotation-save-draft-button"
                >
                  <i className="bi bi-save me-2"></i>
                  {isCreateProjectMode && !isEditMode
                    ? 'Crear Proyecto (Borrador)'
                    : 'Guardar Borrador'}
                </Dropdown.Item>
                <Dropdown.Item onClick={handleCancel}>
                  <i className="bi bi-x-circle me-2"></i>
                  {embedded ? 'Limpiar Formulario' : 'Cancelar'}
                </Dropdown.Item>
                {isEditMode && quotationStatus === QUOTATION_STATUS.APPROVED && (
                  <Dropdown.Item onClick={handleSendToClient} data-testid="quotation-send-to-client-button">
                    <i className="bi bi-send me-2"></i>Marcar como Enviada
                  </Dropdown.Item>
                )}
                {isEditMode && (
                  <>
                    <Dropdown.Divider />
                    <Dropdown.Item
                      className="text-danger"
                      onClick={() => setShowDeleteModal(true)}
                      disabled={deleting}
                    >
                      <i className="bi bi-trash me-2"></i>Eliminar Cotización
                    </Dropdown.Item>
                  </>
                )}
              </KebabMenu>
            </Card.Header>
            <Card.Body>
              {/* Project/Client Selection based on mode */}
              {isCreateProjectMode ? (
                // Create Project Mode: Show project fields
                <>
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>
                          Nombre del Proyecto <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          value={projectTitle}
                          onChange={(e) => setProjectTitle(e.target.value)}
                          placeholder="Ej: Instalación de Tanque de Combustible"
                          disabled={loading || (isEditMode && projectId)}
                          data-testid="project-title-input"
                        />
                        <Form.Text className="text-muted">
                          El nombre con el que identificarás el proyecto — es lo que se mostrará en grande.
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>
                          Cliente <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                          value={clientId}
                          onChange={(e) => setClientId(e.target.value)}
                          disabled={loadingClients || loading || (isEditMode && projectId)}
                          data-testid="project-client-select"
                        >
                          <option value="">Seleccionar cliente...</option>
                          {clients.map((client) => (
                            <option key={client.id} value={client.id}>
                              {client.company_name || client.primary_contact_name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="mb-4">
                    <Col md={8}>
                      <Form.Group>
                        <Form.Label>Descripción del Proyecto</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          value={projectDescription}
                          onChange={(e) => setProjectDescription(e.target.value)}
                          placeholder="Descripción detallada del proyecto..."
                          disabled={loading || (isEditMode && projectId)}
                          data-testid="project-description-textarea"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Tipo de Proyecto</Form.Label>
                        <Form.Select
                          value={projectType}
                          onChange={(e) => setProjectType(e.target.value)}
                          disabled={loading || (isEditMode && projectId)}
                          data-testid="project-type-select"
                        >
                          <option value="">Seleccionar tipo...</option>
                          {projectTypes.map((type) => (
                            <option key={type.id || type.code} value={type.id || type.code}>
                              {type.code} — {type.label}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Text className="text-muted">
                          Clasifica el trabajo y aparece en el código corto del proyecto. No cambia el nombre visible.
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                  {/* #462: read-only preview of the auto-generated project_number.
                      The consecutivo ("###") is filled in atomically by the DB on
                      save — it cannot be previewed client-side by design (see
                      projectNumberPreview above). */}
                  {projectNumberPreview && (
                    <Row className="mb-4">
                      <Col md={12}>
                        <Alert variant="light" className="border py-2 mb-0 d-flex align-items-center gap-2">
                          <i className="bi bi-hash text-muted"></i>
                          <span className="text-muted">ID interno del sistema (automático):</span>
                          <code className="fw-bold" data-testid="project-number-preview">{projectNumberPreview}</code>
                        </Alert>
                      </Col>
                    </Row>
                  )}
                </>
              ) : (
                // Standard Mode: Show project selection
                <Row className="mb-4">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>
                        Proyecto <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Select
                        value={projectId}
                        onChange={(e) => setProjectId(e.target.value)}
                        disabled={loadingProjects || isEditMode || loading}
                        data-testid="quotation-project-select"
                      >
                        <option value="">Seleccionar proyecto...</option>
                        {projects.map((project) => (
                          <option key={project.id} value={project.id}>
                            {project.project_number} - {project.title}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
              )}

              {/* RACI Selectors (#129) — Supervisor + Lider */}
              <Row className="mb-4">
                <Col md={6}>
                  <Form.Group>
                    {/* Sin asterisco: `validateForm` (arriba en este mismo
                        archivo) nunca chequea `supervisorId`, asi que la
                        cotizacion se envia igual sin supervisor. Un asterisco
                        que no bloquea nada le ensena al usuario que el
                        asterisco no significa nada. */}
                    <Form.Label>
                      Supervisor responsable
                    </Form.Label>
                    <Form.Select
                      value={supervisorId}
                      onChange={(e) => setSupervisorId(e.target.value)}
                      disabled={
                        loading ||
                        // Supervisor self-fills and locks; non-admin in edit mode cannot change
                        (isSupervisor && !isAdmin) ||
                        (isEditMode && !isAdmin)
                      }
                      data-testid="quotation-supervisor-select"
                    >
                      <option value="">Seleccionar supervisor...</option>
                      {/* If we have supervisor name from quotation but the
                          current user can't list supervisors, still show it */}
                      {isEditMode && supervisorId && supervisorName &&
                        !supervisors.find((s) => s.id === supervisorId) && (
                          <option value={supervisorId}>{supervisorName}</option>
                        )}
                      {supervisors.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.full_name} ({u.role})
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Text className="text-muted">
                      {isSupervisor && !isAdmin
                        ? 'Tu eres el supervisor responsable.'
                        : 'Quién es responsable (accountable) de esta cotización.'}
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Líder técnico</Form.Label>
                    <Form.Select
                      value={liderId}
                      onChange={(e) => setLiderId(e.target.value)}
                      disabled={loading || (isEditMode && quotationStatus !== QUOTATION_STATUS.DRAFT && quotationStatus !== null)}
                      data-testid="quotation-lider-select"
                    >
                      <option value="">Sin asignar (puede asignarse después)</option>
                      {isEditMode && liderId && liderName &&
                        !lideres.find((l) => l.id === liderId) && (
                          <option value={liderId}>{liderName}</option>
                        )}
                      {lideres.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.full_name}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Text className="text-muted">
                      Técnico responsable de la ejecución operativa.
                      {isEditMode && (isSupervisor || isAdmin) && (
                        <>
                          {' '}
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 align-baseline"
                            onClick={() => {
                              setReassignLiderId(liderId || '');
                              setShowReassignLiderModal(true);
                            }}
                            data-testid="quotation-reassign-lider-button"
                          >
                            Reasignar líder
                          </Button>
                        </>
                      )}
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              {/*
                #609 — el cartel que le faltaba a la pared.
                La compuerta del servidor no deja aprobar ni enviar una
                cotización con precios que nadie confirmó, pero hasta acá el
                admin sólo se enteraba al intentar enviar. Esto lo dice ANTES,
                nombra las líneas, y trae el ack que las destraba — la misma
                forma que #593 usa para el margen "a costo".
              */}
              {hasUnconfirmedPrices && (
                <Alert
                  variant="warning"
                  className="mb-3 quotation-builder-alert"
                  data-testid="quotation-unconfirmed-prices-alert"
                >
                  <div className="fw-bold mb-1">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {unconfirmedItems.length === 1
                      ? 'Hay 1 línea con un precio sin confirmar'
                      : `Hay ${unconfirmedItems.length} líneas con precios sin confirmar`}
                  </div>
                  <div className="small mb-2">
                    Esos montos los puso el asistente: no aparecen en lo que dijo
                    el usuario ni salieron del catálogo de inventario. Podés
                    guardar el borrador así, pero la cotización{' '}
                    <strong>no se va a poder aprobar ni marcar como enviada</strong>{' '}
                    hasta que se confirmen.
                  </div>
                  <ul className="small mb-2 ps-3" data-testid="quotation-unconfirmed-prices-list">
                    {unconfirmedItems.map((item, i) => (
                      <li key={`${item.id || item.description}-${i}`}>
                        {item.description || '(sin descripción)'} —{' '}
                        {formatCurrency(item.unit_price || 0)}
                      </li>
                    ))}
                  </ul>
                  <div className="small mb-2">
                    Para destrabarlas: corregí el precio de cada línea con el
                    monto real, o marcá abajo que los montos de arriba son
                    correctos.
                  </div>
                  <Form.Check
                    type="checkbox"
                    id="quotation-unconfirmed-prices-ack"
                    data-testid="quotation-unconfirmed-prices-ack"
                    checked={unconfirmedPricesAck}
                    onChange={(e) => setUnconfirmedPricesAck(e.target.checked)}
                    disabled={loading}
                    label="Revisé estos montos y confirmo que son correctos"
                  />
                </Alert>
              )}

              {/* Items Table */}
              <div className="mb-4">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 gap-2">
                  <h5 className="mb-0">Artículos de la Cotización</h5>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleAddItem}
                    disabled={loading || items.length >= 50}
                    data-testid="quotation-add-item-button"
                    className="d-none d-md-inline-flex"
                  >
                    <i className="bi bi-plus-circle me-1"></i>
                    Agregar Artículo
                  </Button>
                </div>

                {/* Desktop Table View */}
                <div className="quotation-items-table-desktop">
                  <div className="table-responsive">
                    <Table bordered hover size="sm" data-testid="quotation-items-table">
                      <thead className="table-light">
                        <tr>
                          <th>Tipo</th>
                          <th>Descripción</th>
                          <th>Cantidad</th>
                          <th>Unidad</th>
                          <th>Precio Unit.</th>
                          <th className="text-end">Total</th>
                          <th>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item, index) => (
                          <QuotationItemRow
                            key={index}
                            item={item}
                            index={index}
                            onUpdate={handleItemUpdate}
                            onRemove={handleRemoveItem}
                            onDuplicate={handleDuplicateItem}
                            disabled={loading}
                          />
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </div>

                {/* Mobile Cards View */}
                <div className="quotation-items-cards-mobile">
                  {items.map((item, index) => (
                    <QuotationItemCard
                      key={index}
                      item={item}
                      index={index}
                      onUpdate={handleItemUpdate}
                      onRemove={handleRemoveItem}
                      onDuplicate={handleDuplicateItem}
                      disabled={loading}
                    />
                  ))}
                  <Button
                    variant="outline-primary"
                    onClick={handleAddItem}
                    disabled={loading || items.length >= 50}
                    className="w-100 quotation-add-item-button-mobile"
                  >
                    <i className="bi bi-plus-circle me-1"></i>
                    Agregar Artículo
                  </Button>
                </div>

                <Alert variant="info" className="mb-0 mt-3">
                  <small>
                    <i className="bi bi-info-circle me-1"></i>
                    Puedes agregar hasta 50 artículos. Los cálculos se actualizan automáticamente.
                  </small>
                </Alert>
              </div>

              {/* Calculation Section */}
              <Row>
                <Col lg={6}>
                  <Card className="mb-3">
                    <Card.Header>
                      <h6 className="mb-0">Ajustes de Cálculo</h6>
                    </Card.Header>
                    <Card.Body>
                      {/*
                        M1 — los campos de margen se MUESTRAN a todos los roles
                        que llegan a esta pantalla y sólo un admin puede
                        EDITARLOS. Antes estaban detrás de `isAdmin` a secas,
                        mientras el gate de acceso (arriba) admite admin Y
                        técnico: el técnico cotizaba sin ver siquiera que había
                        un margen, y salía 0/0. Ver el número y poder fijarlo
                        son dos permisos distintos; conflarlos fue lo que
                        convirtió una restricción en una pérdida silenciosa.
                      */}
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Porcentaje de Riesgo (%)
                        </Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={riskPercentage}
                          onChange={(e) => setRiskPercentage(
                            e.target.value === '' ? '' : parseFloat(e.target.value)
                          )}
                          disabled={loading || !isAdmin}
                          placeholder="Sin definir"
                          data-testid="quotation-risk-percentage-input"
                        />
                        <Form.Text className="text-muted">
                          Porcentaje adicional para cubrir riesgos del proyecto
                        </Form.Text>
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>
                          Porcentaje de Ganancia (%)
                        </Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={profitPercentage}
                          onChange={(e) => setProfitPercentage(
                            e.target.value === '' ? '' : parseFloat(e.target.value)
                          )}
                          disabled={loading || !isAdmin}
                          placeholder="Sin definir"
                          data-testid="quotation-profit-percentage-input"
                        />
                        <Form.Text className="text-muted">
                          Margen de ganancia deseado
                        </Form.Text>
                      </Form.Group>

                      {!isAdmin && (
                        <Form.Text
                          className="d-block mb-3 text-muted"
                          data-testid="quotation-margin-readonly-note"
                        >
                          <i className="bi bi-lock me-1"></i>
                          El margen lo fija un administrador. Podés ver con qué
                          margen va la cotización, pero no cambiarlo.
                        </Form.Text>
                      )}

                      {/*
                        #593 — dos estados, un solo panel. "Sin definir" (NULL)
                        y "a costo" (0 elegido) dejaron de ser lo mismo: el
                        primero es el estado normal del borrador antes de que
                        Administración ponga el margen, el segundo es una
                        decisión comercial que hay que firmar. El checkbox sigue
                        siendo el que convierte uno en el otro.
                      */}
                      {(isMarginUndefined || isZeroMargin) && (
                        <Alert
                          variant={isZeroMargin ? 'warning' : 'info'}
                          className="mb-3 quotation-builder-alert"
                          data-testid={isZeroMargin ? 'quotation-zero-margin-warning' : 'quotation-undefined-margin-note'}
                        >
                          <div className="fw-bold mb-1">
                            <i className={`bi ${isZeroMargin ? 'bi-exclamation-triangle' : 'bi-info-circle'} me-2`}></i>
                            {isZeroMargin
                              ? 'Esta cotización va sin margen'
                              : 'El margen todavía no está definido'}
                          </div>
                          <div className="small mb-2">
                            {isZeroMargin
                              ? '0% de riesgo y 0% de ganancia: se cobra el costo.'
                              : 'Podés guardar el borrador así. El % de riesgo y el % de utilidad los define Administración antes de enviar la cotización, y hasta entonces no se puede enviar.'}
                            {isAdmin
                              ? ' Fijá los porcentajes arriba, o confirmá que se emite a costo.'
                              : ' El margen lo fija un administrador.'}
                          </div>
                          <Form.Check
                            type="checkbox"
                            id="quotation-zero-margin-ack"
                            data-testid="quotation-zero-margin-ack"
                            checked={zeroMarginAck}
                            onChange={(e) => setZeroMarginAck(e.target.checked)}
                            disabled={loading}
                            label="Confirmo que esta cotización se emite a costo"
                          />
                        </Alert>
                      )}

                      <Form.Group className="mb-0">
                        <Form.Label>
                          Tasa de Impuesto (%)
                        </Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={taxRate}
                          onChange={(e) => setTaxRate(parseFloat(e.target.value) || 0)}
                          disabled={loading}
                        />
                        <Form.Text className="text-muted">
                          IVA u otro impuesto aplicable (default: 13%)
                        </Form.Text>
                      </Form.Group>
                    </Card.Body>
                  </Card>

                  <Form.Group>
                    <Form.Label>Notas / Comentarios</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Notas adicionales para la cotización..."
                      disabled={loading}
                    />
                  </Form.Group>
                </Col>

                <Col lg={6}>
                  <Card className="bg-light">
                    <Card.Header>
                      <h6 className="mb-0">
                        <i className="bi bi-calculator me-2"></i>
                        Resumen de Totales
                      </h6>
                    </Card.Header>
                    <Card.Body data-testid="quotation-totals-summary">
                      <Table borderless className="mb-0">
                        <tbody>
                          <tr>
                            <td>Subtotal de Artículos:</td>
                            <td className="text-end">
                              <strong>{formatCurrency(totals.itemsSubtotal)}</strong>
                            </td>
                          </tr>
                          {/*
                            M1 — estas filas eran admin-only junto con los
                            inputs. Se abren al mismo público por coherencia y
                            porque el recorte no recortaba nada: "Subtotal de
                            Artículos" y "Base (antes de impuestos)" ya se
                            muestran a todos los roles acá abajo, y el margen es
                            base/subtotal − 1. Esconder los dos porcentajes
                            mientras se muestran los dos números de los que se
                            derivan no es una frontera de confidencialidad, es
                            la apariencia de una — y fue lo que dejó al técnico
                            cotizando a ciegas. La frontera real de costos
                            internos en este repo es `canSeeInternalCosts`
                            (gastos, #518), otro eje.
                          */}
                          {resolvedRiskPercentage > 0 && (
                            <tr className="text-muted">
                              <td>
                                + Riesgo ({resolvedRiskPercentage}%):
                              </td>
                              <td className="text-end">
                                {formatCurrency(totals.riskAmount)}
                              </td>
                            </tr>
                          )}
                          {resolvedProfitPercentage > 0 && (
                            <tr className="text-muted">
                              <td>
                                + Ganancia ({resolvedProfitPercentage}%):
                              </td>
                              <td className="text-end">
                                {formatCurrency(totals.profitAmount)}
                              </td>
                            </tr>
                          )}
                          <tr className="border-top">
                            <td>Base (antes de impuestos):</td>
                            <td className="text-end">
                              <strong>{formatCurrency(totals.baseAmount)}</strong>
                            </td>
                          </tr>
                          <tr className="text-muted">
                            <td>+ IVA ({taxRate}%):</td>
                            <td className="text-end">
                              {formatCurrency(totals.taxAmount)}
                            </td>
                          </tr>
                          <tr className="border-top">
                            <td>
                              <h5 className="mb-0">TOTAL:</h5>
                            </td>
                            <td className="text-end">
                              <h5 className="mb-0 text-primary">
                                {formatCurrency(totals.total)}
                              </h5>
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>

                  {/* Decía "Wave Calculator Integrado: los cálculos siguen
                      exactamente la lógica del Wave Calculator de ProMedic".
                      "Wave Calculator" es el nombre de la planilla interna de la
                      que salió la fórmula: no le dice nada a quien cotiza y no
                      hay nada que hacer con esa información. Lo que sí sirve es
                      el orden en que se aplican los porcentajes. */}
                  <Alert variant="success" className="mt-3 mb-0">
                    <small>
                      <i className="bi bi-check-circle me-1"></i>
                      <strong>Orden del cálculo:</strong> primero se suman materiales, mano de obra y
                      herramientas; sobre ese subtotal se aplica el riesgo, después la utilidad y por
                      último el impuesto.
                    </small>
                  </Alert>

                  {isCreateProjectMode && !isEditMode && (
                    <Alert variant="warning" className="mt-3">
                      <small>
                        <i className="bi bi-exclamation-triangle me-1"></i>
                        <strong>Nota:</strong> Se creará un nuevo proyecto con el número único que será compartido con esta cotización.
                      </small>
                    </Alert>
                  )}
                </Col>
              </Row>

              {/* Action Buttons */}
              <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
                {/* Approval/Rejection buttons for admin when quotation is under review */}
                {isEditMode && isAdmin && quotationStatus === QUOTATION_STATUS.UNDER_REVIEW && (
                  <>
                    <Button
                      variant="danger"
                      onClick={() => setShowRejectModal(true)}
                      disabled={loading || rejecting || approving}
                      data-testid="quotation-reject-button"
                    >
                      <i className="bi bi-x-circle me-1"></i>
                      Rechazar
                    </Button>
                    <Button
                      variant="success"
                      onClick={handleApproveQuotation}
                      disabled={loading || approving || rejecting}
                      data-testid="quotation-approve-button"
                    >
                      {approving ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Aprobando...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-check-circle me-1"></i>
                          Aprobar Cotización
                        </>
                      )}
                    </Button>
                  </>
                )}

                {/* Save as draft button (shown when not under review or for new quotations) */}
                {(!isEditMode || quotationStatus === QUOTATION_STATUS.DRAFT || quotationStatus === null) && (
                  <Button
                    variant="outline-secondary"
                    onClick={handleSaveDraft}
                    disabled={loading}
                    data-testid="quotation-save-draft-main-button"
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Guardando...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-floppy me-1"></i>
                        Guardar Borrador
                      </>
                    )}
                  </Button>
                )}

                {/* Submit for review button (shown when not under review or for new quotations) */}
                {(!isEditMode || quotationStatus === QUOTATION_STATUS.DRAFT || quotationStatus === null) && (
                  <Button
                    variant="primary"
                    onClick={handleSubmitForReview}
                    disabled={loading}
                    data-testid="quotation-submit-button"
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Enviando...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-send me-1"></i>
                        {isCreateProjectMode && !isEditMode
                          ? 'Crear y Enviar para Revisión'
                          : 'Enviar para Revisión'}
                      </>
                    )}
                  </Button>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Send to Client Modal */}
      {/*
        #A4: este modal se llamaba "Enviar Cotización a Cliente" y traía un
        textarea "Mensaje Personalizado" y un checkbox "Adjuntar PDF" tildado
        por defecto. Ninguno de los dos se usaba en ninguna parte:
        `handleConfirmSend` sólo llama `markQuotationSent(id)`. El usuario
        escribía un mensaje para el cliente, marcaba "Adjuntar PDF", confirmaba
        — y no salía ningún correo ni se guardaba su texto. No hay
        infraestructura de email ni generador de PDF de cotización en todo
        `src/`, así que no era un cable suelto: era una promesa entera sin
        respaldo.

        Lo que la acción SÍ hace es registrar el envío (status `sent` +
        `sent_at`, y el proyecto a `quotation_sent`). El modal dice eso ahora.
      */}
      <Modal show={showSendModal} onHide={handleCloseSendModal} size="lg" data-testid="send-quotation-modal">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-send me-2"></i>
            Marcar Cotización como Enviada
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info">
            <i className="bi bi-info-circle me-2"></i>
            Esta acción <strong>registra</strong> que la cotización se envió: la marca como
            enviada y pasa el proyecto a "Cotización Enviada". El envío al cliente se hace
            por fuera del sistema (correo, WhatsApp); ProMedic no manda el correo ni adjunta
            el PDF.
          </Alert>

          <div className="mb-0">
            <h6>Detalles de la Cotización</h6>
            <Table bordered size="sm">
              <tbody>
                <tr>
                  <td><strong>Monto Total:</strong></td>
                  <td className="text-end">{formatCurrency(totals.total)}</td>
                </tr>
                <tr>
                  <td><strong>Contacto del cliente:</strong></td>
                  <td>{clientEmail}</td>
                </tr>
              </tbody>
            </Table>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseSendModal} disabled={loading}>
            Cancelar
          </Button>
          <Button variant="success" onClick={handleConfirmSend} disabled={loading}>
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Guardando...
              </>
            ) : (
              <>
                <i className="bi bi-send me-1"></i>
                Marcar como Enviada
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Rejection Reason Modal */}
      <Modal
        show={showRejectModal}
        onHide={() => { setShowRejectModal(false); setRejectReason(''); }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">
            <i className="bi bi-x-circle me-2"></i>
            Rechazar Cotización
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            <i className="bi bi-exclamation-triangle me-2"></i>
            La cotización será marcada como rechazada y el creador será notificado.
          </Alert>
          <Form.Group>
            <Form.Label>
              Razón del Rechazo <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Explica por qué se rechaza esta cotización..."
              data-testid="quotation-reject-reason"
              autoFocus
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => { setShowRejectModal(false); setRejectReason(''); }}
            disabled={rejecting}
          >
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={handleRejectQuotation}
            disabled={rejecting || !rejectReason.trim()}
            data-testid="quotation-reject-confirm-button"
          >
            {rejecting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Rechazando...
              </>
            ) : (
              <>
                <i className="bi bi-x-circle me-1"></i>
                Confirmar Rechazo
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Reassign Lider Modal (#129) */}
      <Modal
        show={showReassignLiderModal}
        onHide={() => { setShowReassignLiderModal(false); setReassignLiderId(''); }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-person-gear me-2"></i>
            Reasignar Líder Técnico
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info">
            <i className="bi bi-info-circle me-2"></i>
            Solo el supervisor responsable o un admin pueden reasignar al líder técnico.
          </Alert>
          <Form.Group>
            <Form.Label>
              Nuevo líder técnico <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              value={reassignLiderId}
              onChange={(e) => setReassignLiderId(e.target.value)}
              data-testid="quotation-reassign-lider-select"
              autoFocus
            >
              <option value="">Seleccionar líder...</option>
              {lideres.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.full_name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => { setShowReassignLiderModal(false); setReassignLiderId(''); }}
            disabled={reassigning}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleReassignLider}
            disabled={reassigning || !reassignLiderId}
            data-testid="quotation-reassign-lider-confirm"
          >
            {reassigning ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Reasignando...
              </>
            ) : (
              <>
                <i className="bi bi-check-circle me-1"></i>
                Confirmar
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal (Double Confirmation) */}
      <Modal
        show={showDeleteModal}
        onHide={() => { setShowDeleteModal(false); setDeleteConfirmText(''); }}
        centered
      >
        <Modal.Header closeButton className="border-danger">
          <Modal.Title className="text-danger">
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            Eliminar Cotización
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="danger">
            <strong>Esta acción es irreversible.</strong> Se eliminarán permanentemente la cotización y todos sus artículos asociados.
          </Alert>
          <p>Para confirmar, escribe <strong>ELIMINAR</strong> en el campo de abajo:</p>
          <Form.Control
            type="text"
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder="Escribe ELIMINAR para confirmar"
            autoFocus
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => { setShowDeleteModal(false); setDeleteConfirmText(''); }}
            disabled={deleting}
          >
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteQuotation}
            disabled={deleting || deleteConfirmText !== 'ELIMINAR'}
          >
            {deleting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Eliminando...
              </>
            ) : (
              <>
                <i className="bi bi-trash me-1"></i>
                Confirmar Eliminación
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
      </Container>
    </DashboardLayout>
  );
};

QuotationBuilder.propTypes = {
  embedded: PropTypes.bool,
  mode: PropTypes.oneOf(['standard', 'create_project', 'edit']),
  onSuccess: PropTypes.func,
};

export default QuotationBuilder;